import { calculateGaps, enrichPolicy, formatMoney, getMainCoverage, getNextPaymentDateText, getTypeName } from './calculations.js'
import { getBeneficiaryDisplayText, getBeneficiaryReminderPolicies, getDeathBenefitConfirmationPolicies, getDeathBenefitStatusLabel } from './beneficiary.js'

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]))

const reminderText = (statusInfo) => {
  if (statusInfo.diffDays === null || statusInfo.diffDays === undefined) return statusInfo.status || '待确认'
  if (statusInfo.diffDays > 0) return `${statusInfo.diffDays} 天后`
  if (statusInfo.diffDays === 0) return '今日'
  return `逾期 ${Math.abs(statusInfo.diffDays)} 天`
}

export const buildReportHtml = ({ policies, goals, adjustments, familyName = '客户', aiHtml = '', stylesheetUrl = '/pdf-report.css', scriptUrl = '/pdf-report.js' }) => {
  const reportPolicies = policies.map((policy) => enrichPolicy(policy))
  const activePolicies = reportPolicies.filter((policy) => policy.statusInfo.code !== 'lapsed')
  const reportGaps = calculateGaps(policies, goals, adjustments)
  const reportReminders = reportPolicies.filter((policy) => ['pending', 'grace', 'suspended', 'unknown'].includes(policy.statusInfo.code)).slice(0, 8)
  const beneficiaryReminders = getBeneficiaryReminderPolicies(reportPolicies)
  const deathBenefitConfirmationReminders = getDeathBenefitConfirmationPolicies(reportPolicies)
  const generatedAt = new Date().toLocaleDateString('zh-CN')
  const reportAnnualPremium = activePolicies.reduce((sum, policy) => sum + (Number(policy.totalPremium) || 0), 0)

  const rows = reportPolicies.map((policy) => `
    <tr>
      <td>${escapeHtml(policy.productName || '未命名保单')}<br><span>${escapeHtml(policy.company || '-')}</span></td>
      <td>${escapeHtml(policy.applicant || '-')}</td>
      <td>${escapeHtml(policy.insuredPerson || '-')}</td>
      <td>${escapeHtml(getDeathBenefitStatusLabel(policy.deathBenefitStatus))}</td>
      <td>${escapeHtml(getBeneficiaryDisplayText(policy))}</td>
      <td>${escapeHtml(getTypeName(policy.type))}</td>
      <td>${escapeHtml(policy.guaranteePeriod || '-')}<br><span>交 ${escapeHtml(policy.paymentTerm || '-')} 年</span></td>
      <td class="num">¥${formatMoney(policy.totalPremium)}</td>
      <td>${escapeHtml(getNextPaymentDateText(policy))}</td>
      <td class="num">¥${formatMoney(getMainCoverage(policy))}</td>
      <td><span class="badge">${escapeHtml(policy.statusInfo.status || '-')}</span></td>
    </tr>
  `).join('') || '<tr><td colspan="11" class="empty">当前视图下暂无保单</td></tr>'

  const gapCards = reportGaps.map((gap) => {
    const target = Number(gap.target) || 0
    const current = Number(gap.current) || 0
    const shortage = Math.max(0, target - current)
    const ratio = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : (current > 0 ? 100 : 0)
    return `
      <div class="gap-card">
        <div class="gap-top"><strong>${escapeHtml(gap.name)}</strong><span>${ratio}%</span></div>
        <progress class="bar" max="100" value="${ratio}" aria-label="${escapeHtml(gap.name)}达成比例 ${ratio}%">${ratio}%</progress>
        <div class="gap-grid">
          <div><span>目标</span><b>¥${formatMoney(target)}</b></div>
          <div><span>已配置</span><b>¥${formatMoney(current)}</b></div>
          <div><span>缺口</span><b class="${shortage > 0 ? 'risk' : 'ok'}">¥${formatMoney(shortage)}</b></div>
        </div>
      </div>
    `
  }).join('')

  const reminderRows = reportReminders.map((policy) => `
    <tr>
      <td>${escapeHtml(policy.insuredPerson || '-')}</td>
      <td>${escapeHtml(policy.productName || '未命名保单')}</td>
      <td>${escapeHtml(policy.statusInfo.status || '-')}</td>
      <td>${escapeHtml(reminderText(policy.statusInfo))}</td>
      <td class="num">¥${formatMoney(policy.totalPremium)}</td>
    </tr>
  `).join('') || '<tr><td colspan="5" class="empty">当前没有 30 天内待缴费或逾期提醒</td></tr>'

  const beneficiaryReminderRows = beneficiaryReminders.map((policy) => `
    <tr>
      <td>${escapeHtml(policy.insuredPerson || '-')}</td>
      <td>${escapeHtml(policy.productName || '未命名保单')}<br><span>${escapeHtml(policy.company || '-')}</span></td>
      <td>${escapeHtml(policy.beneficiary || '法定')}</td>
      <td>联系保险公司，将法定受益人变更为指定受益人</td>
    </tr>
  `)
  const deathBenefitConfirmationRows = deathBenefitConfirmationReminders.map((policy) => `
    <tr>
      <td>${escapeHtml(policy.insuredPerson || '-')}</td>
      <td>${escapeHtml(policy.productName || '未命名保单')}<br><span>${escapeHtml(policy.company || '-')}</span></td>
      <td>身故责任待确认</td>
      <td>查看保险合同，确认主险或附加险是否包含身故给付责任</td>
    </tr>
  `)
  const contractReminderRows = [...deathBenefitConfirmationRows, ...beneficiaryReminderRows].join('') || '<tr><td colspan="4" class="empty">当前没有待处理的身故责任或受益人提醒</td></tr>'

  const aiSection = aiHtml
    ? `<section class="section break-before"><h2>AI 家庭保障检视建议</h2><div class="markdown-body">${aiHtml}</div></section>`
    : '<section class="section note"><h2>后续建议</h2><p>如需更完整的缺口建议，可先点击页面中的「AI 智能分析」生成检视报告，再从报告弹窗导出 PDF。</p></section>'

  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
<meta charset="UTF-8">
<title>${escapeHtml(familyName)}家庭保障检视报告</title>
<link rel="stylesheet" href="${escapeHtml(stylesheetUrl)}">
<script src="${escapeHtml(scriptUrl)}" defer></script>
</head>
<body>
<div class="report-actions" role="region" aria-label="保存 PDF">
  <div><strong>报告已准备好</strong><span>点击右侧按钮，在打印窗口中选择“另存为 PDF”或“存储到文件”。</span></div>
  <button id="save-pdf-button" type="button">保存为 PDF / 打印</button>
</div>
<div class="report">
  <header class="cover">
    <div><h1>${escapeHtml(familyName)}家庭保障检视报告</h1><p>基于当前看板数据生成，用于快速了解已购保险、保费责任、保障缺口和后续配置方向。</p></div>
    <div class="meta">生成日期：${escapeHtml(generatedAt)}<br>数据范围：${escapeHtml(familyName)}</div>
  </header>
  <section class="summary">
    <div class="summary-card"><span>保单数量</span><strong>${reportPolicies.length}</strong></div>
    <div class="summary-card"><span>有效保单</span><strong>${activePolicies.length}</strong></div>
    <div class="summary-card"><span>年交总保费</span><strong>¥${formatMoney(reportAnnualPremium)}</strong></div>
  </section>
  <section class="section">
    <h2>一、已购保单基本信息</h2>
    <table class="policy-table"><thead><tr><th>产品/公司</th><th>投保人</th><th>被保人</th><th>身故责任</th><th>受益人</th><th>险种</th><th>期限</th><th>年交保费</th><th>下次缴费日</th><th>核心保额</th><th>状态</th></tr></thead><tbody>${rows}</tbody></table>
  </section>
  <section class="section">
    <h2>二、保障目标与缺口</h2>
    <div class="gap-wrap">${gapCards}</div>
  </section>
  <section class="section">
    <h2>三、近期缴费与状态提醒</h2>
    <table class="reminder-table"><thead><tr><th>被保人</th><th>产品名称</th><th>状态</th><th>时间提醒</th><th>应缴年保费</th></tr></thead><tbody>${reminderRows}</tbody></table>
  </section>
  <section class="section beneficiary-reminder-section">
    <h2>四、身故责任与受益人提醒</h2>
    <table class="beneficiary-reminder-table"><thead><tr><th>被保人</th><th>产品/公司</th><th>当前情况</th><th>建议行动</th></tr></thead><tbody>${contractReminderRows}</tbody></table>
  </section>
  ${aiSection}
  <footer class="footer">说明：本报告根据本地录入数据自动生成，金额和保障责任以正式合同、批单、保险公司系统记录为准。AI 建议仅作辅助参考，不构成投保、退保或理赔承诺。</footer>
</div>
</body>
</html>`
}

export const printReport = (options) => {
  const reportWindow = window.open('', '_blank')
  if (!reportWindow) throw new Error('浏览器阻止了打印窗口，请允许弹窗后重试。')
  reportWindow.opener = null
  reportWindow.document.open()
  reportWindow.document.write(buildReportHtml({
    ...options,
    stylesheetUrl: new URL('/pdf-report.css', window.location.href).href,
    scriptUrl: new URL('/pdf-report.js', window.location.href).href,
  }))
  reportWindow.document.close()
}
